import {
    ActionIcon,
    Box,
    Button,
    FileButton,
    Group,
    Image,
    LoadingOverlay,
    Select,
    Stack,
    Text,
    Textarea,
    TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconPhoto, IconTrash } from "@tabler/icons-react";
import type { JSX } from "react";
import { useEffect } from "react";
import { useImageSelect } from "../../hooks/useImageSelect";
import type { ItemRole, ItemType } from "../../utils";
import classes from "./ItemForm.module.css";
import ToolArgumentsInput from "./ToolArgumentsInput";

export interface IFormValues {
    type: Exclude<ItemType, "REASONING">; // REASONING not addable/editable
    role: ItemRole; // used only when type === "MESSAGE"
    content: string; // required for MESSAGE and FUNCTION_CALL_OUTPUT
    toolCallId: string; // required for FUNCTION_CALL and FUNCTION_CALL_OUTPUT
    toolName: "" | "documentSearch" | "readDocument"; // required (non-empty) for FUNCTION_CALL
    toolArguments: Record<string, string>; // required (non-empty) for FUNCTION_CALL
    imageB64: string | null;
    imageName: string;
}

interface IProps {
    handleSubmit: (values: IFormValues) => void;
    initialValues?: IFormValues;
    fetching: boolean;
}

const TYPES: Exclude<ItemType, "REASONING">[] = ["MESSAGE", "FUNCTION_CALL", "FUNCTION_CALL_OUTPUT"];
const ROLES: ItemRole[] = ["USER", "ASSISTANT"];
const TOOL_NAMES = ["documentSearch", "readDocument"] as const;

function ItemForm({ handleSubmit, initialValues, fetching }: IProps): JSX.Element {
    const form = useForm<IFormValues>({
        initialValues: initialValues ?? {
            type: "MESSAGE",
            role: "USER" as ItemRole,
            content: "",
            toolCallId: "",
            toolName: "",
            toolArguments: {},
            imageB64: null,
            imageName: "",
        },
        validate: {
            type: (value) => (TYPES.includes(value) ? null : "Required"),
            role: (value, values) => {
                if (values.type === "MESSAGE") {
                    return ROLES.includes(value) ? null : "Required";
                }
                return null;
            },
            content: (value, values) => {
                if (values.type === "MESSAGE" || values.type === "FUNCTION_CALL_OUTPUT") {
                    return value ? null : "Required";
                }
                return null; // FUNCTION_CALL doesn't require content
            },
            toolName: (value, values) => {
                if (values.type === "FUNCTION_CALL") {
                    return value ? null : "Required";
                }
                return null;
            },
            toolCallId: (value, values) => {
                if (values.type === "FUNCTION_CALL" || values.type === "FUNCTION_CALL_OUTPUT") {
                    return value ? null : "Required";
                }
                return null;
            },
            toolArguments: (value, values) => {
                if (values.type === "FUNCTION_CALL") {
                    return Object.keys(value).length === 0 ? "Required" : null;
                }
                return null;
            },
        },
    });

    const {
        value: imageValue,
        preview: imagePreview,
        name: imageName,
        onSelect,
        clear,
        error,
    } = useImageSelect<string>({
        initialValue: initialValues?.imageB64 ?? null,
        initialName: initialValues?.imageName ?? "",
        initialPreview: initialValues?.imageB64 ?? null,
        toValue: (file) =>
            new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    const result = reader.result as string;
                    resolve({ value: result, preview: result, name: file.name });
                };
                reader.onerror = () => reject(new Error("Failed to read file"));
                reader.readAsDataURL(file);
            }),
        validate: (file) => {
            const validTypes = ["image/png", "image/jpeg", "image/webp"];
            if (!validTypes.includes(file.type)) {
                return "Only PNG, JPEG, and WebP images are supported";
            }
            return null;
        },
    });

    useEffect(() => {
        if (form.values.imageB64 !== imageValue) {
            form.setFieldValue("imageB64", imageValue);
        }
        if (form.values.imageName !== imageName) {
            form.setFieldValue("imageName", imageName);
        }
        const currentImageError = form.errors.imageB64;
        if (error) {
            if (currentImageError !== error) {
                form.setFieldError("imageB64", error);
            }
        } else if (currentImageError) {
            form.clearFieldError("imageB64");
        }
    }, [form, imageName, imageValue, error]);

    const showRole = form.values.type === "MESSAGE";
    const showContent = form.values.type === "MESSAGE" || form.values.type === "FUNCTION_CALL_OUTPUT";
    const showToolName = form.values.type === "FUNCTION_CALL";
    const showToolCallId = form.values.type === "FUNCTION_CALL" || form.values.type === "FUNCTION_CALL_OUTPUT";
    const showToolArguments = form.values.type === "FUNCTION_CALL";
    const showImage = form.values.type === "MESSAGE";

    return (
        <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack maw={800} mx="auto">
                <LoadingOverlay visible={fetching} />
                <Select withAsterisk label="Item type" data={TYPES} {...form.getInputProps("type")} />
                {showRole && <Select withAsterisk label="Item role" data={ROLES} {...form.getInputProps("role")} />}
                {showContent && (
                    <Textarea
                        withAsterisk
                        autosize
                        minRows={3}
                        label="Item content"
                        {...form.getInputProps("content")}
                    />
                )}
                {showImage && (
                    <Stack gap="xs">
                        <Group gap="xs">
                            <FileButton accept="image/png,image/jpeg,image/webp" onChange={onSelect}>
                                {(props) => (
                                    <ActionIcon {...props} size={36} variant="light">
                                        <IconPhoto />
                                    </ActionIcon>
                                )}
                            </FileButton>
                            <Text size="sm">Attach image (optional)</Text>
                        </Group>
                        {imagePreview && (
                            <Box className={classes.previewWrapper}>
                                <Box className={classes.previewBox}>
                                    <Image src={imagePreview} w={128} h={128} radius="md" alt={form.values.imageName} />
                                    <ActionIcon
                                        variant="light"
                                        className={classes.trash}
                                        onClick={clear}
                                        aria-label="Remove image"
                                    >
                                        <IconTrash />
                                    </ActionIcon>
                                </Box>
                                <Text size="sm" className={classes.filename}>
                                    {form.values.imageName}
                                </Text>
                            </Box>
                        )}
                    </Stack>
                )}
                {showToolName && (
                    <Select withAsterisk label="Tool Name" data={TOOL_NAMES} {...form.getInputProps("toolName")} />
                )}
                {showToolCallId && (
                    <TextInput withAsterisk label="Tool Call ID" {...form.getInputProps("toolCallId")} />
                )}
                {showToolArguments && (
                    <Box>
                        <Box mb="xs">Tool Arguments</Box>
                        <ToolArgumentsInput
                            value={form.values.toolArguments}
                            onChange={(value) => form.setFieldValue("toolArguments", value)}
                        />
                    </Box>
                )}
                <Group justify="flex-end" mt="md">
                    <Button type="submit" disabled={fetching}>
                        Save
                    </Button>
                </Group>
            </Stack>
        </form>
    );
}

export default ItemForm;
